import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/lib/tenant";
import { toast } from "sonner";
import { Tractor, KeyRound, Loader2, CheckCircle2, ExternalLink } from "lucide-react";

export default function FleetConnect() {
  const { api } = useTenant();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [form, setForm] = useState({
    client_id: "",
    client_secret: "",
    subscription_key: "",
    redirect_uri: "https://wilson-contractors.co.uk/api/auth/cnh/callback",
  });

  const load = async () => {
    try { const r = await api.get("/fieldops/credentials"); setStatus(r.data); } catch {/* */}
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.client_secret || !form.subscription_key) {
      toast.error("All three credentials are required"); return;
    }
    setBusy(true);
    try {
      await api.post("/fieldops/credentials", form);
      toast.success("Production credentials saved");
      setForm({ ...form, client_id: "", client_secret: "", subscription_key: "" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const startOAuth = async () => {
    setConnecting(true);
    try {
      const r = await api.get("/auth/cnh/login");
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "OAuth start failed");
      setConnecting(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-widest mb-1">
          <Tractor size={13} /> FieldOps · New Holland
        </div>
        <h1 className="text-2xl font-bold">Connect to your fleet</h1>
        <p className="text-slate-400 text-sm mt-1">Save your production CNH credentials, then sign in with your MyPLM Connect account to link your real machines.</p>
      </div>

      {/* Step 1 — credentials */}
      <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-6 mb-5">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-6 h-6 grid place-items-center rounded-full bg-amber-400 text-[#0a0e1c] text-xs font-bold">1</span>
          <h2 className="font-semibold">Save production credentials</h2>
          {status?.client_id_set && <CheckCircle2 size={16} className="text-emerald-400 ml-auto" />}
        </div>
        {status?.client_id_set ? (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-3 text-xs text-emerald-200 mt-3">
            <div><strong>Saved.</strong> client_id <code>{status.client_id_preview}</code></div>
            {status.saved_at && <div className="text-slate-400 mt-0.5">on {new Date(status.saved_at).toLocaleString("en-GB")}</div>}
            <div className="mt-2">Paste new values below to rotate.</div>
          </div>
        ) : (
          <p className="text-xs text-slate-400 mt-1 mb-3">Find these on develop.cnh.com → Wilson App → <strong>Prod details</strong> button.</p>
        )}
        <form onSubmit={save} className="space-y-3 mt-4">
          <Input label="Production client_id" value={form.client_id} onChange={v => setForm({...form, client_id: v})} testid="cnh-client-id" />
          <Input label="Production client_secret" value={form.client_secret} onChange={v => setForm({...form, client_secret: v})} testid="cnh-client-secret" mono password />
          <Input label="Production subscription key" value={form.subscription_key} onChange={v => setForm({...form, subscription_key: v})} testid="cnh-sub-key" mono password />
          <Input label="OAuth callback URL (must match CNH portal)" value={form.redirect_uri} onChange={v => setForm({...form, redirect_uri: v})} testid="cnh-redirect" />
          <button type="submit" disabled={busy} data-testid="cnh-save"
                  className="bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-[#0a0e1c] font-semibold rounded-md px-5 py-2.5 text-sm flex items-center gap-2 transition-colors">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><KeyRound size={14} /> Save credentials</>}
          </button>
        </form>
      </div>

      {/* Step 2 — OAuth */}
      <div className={`bg-[#0a0e1c] border rounded-lg p-6 ${status?.client_id_set ? "border-white/10" : "border-white/5 opacity-60"}`}>
        <div className="flex items-center gap-2 mb-3">
          <span className="w-6 h-6 grid place-items-center rounded-full bg-amber-400 text-[#0a0e1c] text-xs font-bold">2</span>
          <h2 className="font-semibold">Sign in to MyPLM Connect</h2>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          You&apos;ll be redirected to CNH&apos;s login page. Sign in with your MyPLM Connect / New Holland account and authorise &quot;Wilson App&quot; to read your fleet. You never enter your password into this app.
        </p>
        <button onClick={startOAuth} disabled={!status?.client_id_set || connecting} data-testid="cnh-connect"
                className="bg-[#004B98] hover:bg-[#003A7A] disabled:opacity-40 text-white font-semibold rounded-md px-6 py-3 text-sm flex items-center gap-2 transition-colors">
          {connecting ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : <>Connect your New Holland account <ExternalLink size={14} /></>}
        </button>
        {!status?.client_id_set && <div className="text-[11px] text-slate-500 mt-3">Save credentials in step 1 first.</div>}
      </div>

      <button onClick={() => nav("/admin/fleet")} className="text-xs text-slate-400 hover:text-amber-300 mt-6">← Back to fleet</button>
    </div>
  );
}

function Input({ label, value, onChange, testid, mono, password }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-slate-400 mb-1.5 block">{label}</span>
      <input type={password ? "password" : "text"} value={value} onChange={(e) => onChange(e.target.value)}
             data-testid={testid}
             className={`w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-sm ${mono ? "font-mono" : ""} focus:outline-none focus:border-amber-400/60 transition-colors`} />
    </label>
  );
}
